import { SandpackFiles } from "@codesandbox/sandpack-react";
import { wrapWithGrapesJS } from "./grapesjs-wrapper";

export interface FileContent {
    path: string;
    content: string;
    completed: boolean;
}

/**
 * Convert Redux code to Sandpack files format
 */
export const convertReduxCodeToSandpackFiles = (
    code: string, 
    selectedHtmlFile: string = 'index.html',
    cssContent: string = '',
    jsContent: string = '',
    isEditMode: boolean = false
): SandpackFiles => {
    try {
        const parsedCode = JSON.parse(code);
        const files: SandpackFiles = {};
        if (isEditMode) {
            // Edit mode: Only process the selected HTML file and wrap it with GrapesJS
            Object.entries(parsedCode).forEach(([filePath, fileData]: [string, any]) => {
                const content = typeof fileData === 'string' ? fileData : fileData?.code || '';
                
                if (filePath === selectedHtmlFile && filePath.endsWith('.html')) {
                    // Wrap HTML with GrapesJS editor
                    const wrappedContent = wrapWithGrapesJS(content, cssContent, jsContent);
                    files['/index.html'] = { code: wrappedContent };
                }
            });
        } else {
            // Preview mode: Include all files as they are, no CSS injection
            Object.entries(parsedCode).forEach(([filePath, fileData]: [string, any]) => {
                const content = typeof fileData === 'string' ? fileData : fileData?.code || '';
                const sandpackPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
                files[sandpackPath] = { code: content };
            });
        }

        return files;
    } catch (error) {
        console.error('Error converting Redux code to Sandpack files:', error);
        return {};
    }
};

/**
 * Convert file contents map to Sandpack files format (for streaming)
 */
export const convertFileContentsToSandpackFiles = (
    fileContents: Map<string, any>,
    isEditMode: boolean = false
): SandpackFiles => {
    const files: SandpackFiles = {};
    
    if (fileContents.size === 0) {
        return files;
    }

    if (isEditMode) {
        // Process HTML files for edit mode
        const htmlFiles = Array.from(fileContents.keys()).filter(path => path.endsWith('.html'));
        const selectedHtmlFile = htmlFiles.find(f => f.includes('index')) || htmlFiles[0];
        
        if (selectedHtmlFile) {
            const fileData = fileContents.get(selectedHtmlFile);
            const htmlContent = typeof fileData === 'string' ? fileData : fileData?.content || '';
            
            // Extract CSS and JS for edit mode
            let cssContent = '';
            let jsContent = '';

            fileContents.forEach((fileData, filePath) => {
                const content = typeof fileData === 'string' ? fileData : fileData?.content || '';
                if (filePath.endsWith('.css')) {
                    cssContent += `\n/* === ${filePath} === */\n${content}\n`;
                } else if (filePath.endsWith('.js')) {
                    jsContent += `\n/* === ${filePath} === */\n${content}\n`;
                }
            });
            
            const wrappedContent = wrapWithGrapesJS(htmlContent, cssContent.trim(), jsContent.trim());
            files['/index.html'] = { code: wrappedContent };
        }
    } else {
        // Not in edit mode - include all files as they are, no CSS injection
        fileContents.forEach((fileData, filePath) => {
            const content = typeof fileData === 'string' ? fileData : fileData?.content || '';
            const sandpackPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
            files[sandpackPath] = { code: content };
        });
    }

    return files;
}; 